import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  inject,
  effect,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  Legend,
  LinearScale,
  PolarAreaController,
  RadialLinearScale,
  Tooltip,
} from 'chart.js';

import { ThemeService } from '../../services/theme.service';
import type { DailyDataPoint, TransactionTypeSummary } from '../../services/dashboard.service';

Chart.register(
  BarController,
  PolarAreaController,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  ArcElement,
  BarElement,
  Legend,
  Tooltip,
);

/** CSS custom properties (theme tokens) used for the polar segments, in order. */
const TYPE_POLAR_TOKENS = [
  '--c-primary',
  '--c-info',
  '--c-success',
  '--c-warning',
  '--c-brand',
  '--c-danger',
  '--c-muted',
  '--c-fg-secondary',
];

function cssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/** Apply alpha to a resolved token colour (hex tokens; falls back to colour-mix). */
function withAlpha(color: string, alpha: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(color);
  if (m) {
    const n = parseInt(m[1], 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
  }
  return `color-mix(in srgb, ${color} ${Math.round(alpha * 100)}%, transparent)`;
}

/**
 * Chart.js bar (deposits vs payouts by day) and polar area (transaction types) with shared external tooltip.
 */
@Component({
  selector: 'app-dashboard-transaction-charts',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './dashboard-transaction-charts.component.html',
})
export class DashboardTransactionChartsComponent implements AfterViewInit, OnChanges, OnDestroy {
  /** Daily series from dashboard charts API. */
  @Input({ required: true }) daily: DailyDataPoint[] = [];

  /** Transaction type counts for polar area. */
  @Input({ required: true }) transactionTypes: TransactionTypeSummary[] = [];

  /** Accessible label for the chart region (matches dashboard section title). */
  @Input() chartTitle = '';
  @Input() showDirectDebit = false;

  @ViewChild('barCanvas') private barCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('polarCanvas') private polarCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('externalTooltip') private externalTooltip?: ElementRef<HTMLDivElement>;

  private barChart: Chart<'bar'> | null = null;
  private polarChart: Chart<'polarArea'> | null = null;
  /** Subset of transaction types with positive count; tooltip indices match polar chart data. */
  private polarDisplayTypes: TransactionTypeSummary[] = [];
  private viewReady = false;
  private readonly theme = inject(ThemeService);

  constructor() {
    effect(() => {
      this.theme.resolved();
      if (!this.viewReady) {
        return;
      }
      this.destroyCharts();
      // Wait for ThemeService to flip the `.dark` class before reading tokens.
      setTimeout(() => this.buildCharts(), 0);
    });
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.buildCharts();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.viewReady) {
      return;
    }
    if (changes['daily'] || changes['transactionTypes']) {
      this.destroyCharts();
      // Defer so *ngIf can add/remove polar canvas before ViewChild resolves.
      setTimeout(() => this.buildCharts(), 0);
    }
  }

  /** True when at least one type has a positive count (polar chart meaningful). */
  hasPolarData(): boolean {
    return this.transactionTypes.some((t) => (t.value ?? 0) > 0);
  }

  ngOnDestroy(): void {
    this.destroyCharts();
  }

  private destroyCharts(): void {
    this.barChart?.destroy();
    this.barChart = null;
    this.polarChart?.destroy();
    this.polarChart = null;
  }

  private formatAmount(amount: number | undefined): string {
    if (amount === undefined || amount === null || Number.isNaN(amount)) {
      return '—';
    }
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  private buildExternalTooltip(chartKind: 'bar' | 'polar') {
    return (context: { chart: Chart; tooltip: { opacity: number; caretX: number; caretY: number; title?: string[]; body?: { lines: string[] }[]; dataPoints: { dataIndex: number }[] } }) => {
      const el = this.externalTooltip?.nativeElement;
      if (!el) {
        return;
      }
      const { chart, tooltip } = context;
      if (tooltip.opacity === 0) {
                el.style.opacity = '0';
        return;
      }

      let html = '';

      if (chartKind === 'bar') {
        const titleLines = tooltip.title ?? [];
        if (titleLines.length) {
          html += `<div class="mb-1.5 font-bold text-brand">${this.escapeHtml(titleLines.join(' '))}</div>`;
        }
        html += '<div class="flex flex-col gap-1">';
        tooltip.body?.forEach((body) => {
          body.lines.forEach((line) => {
            html += `<div class="m-0 flex items-center gap-1.5 text-fg-secondary"><span class="size-1.5 shrink-0 rounded-full bg-info"></span>${this.escapeHtml(line)}</div>`;
          });
        });
        html += '</div>';
        const idx = tooltip.dataPoints[0]?.dataIndex;
        if (idx !== undefined && this.daily[idx]) {
          const d = this.daily[idx];
          const ddp = this.showDirectDebit ? `, ${d.direct_debit_payments_count} direct debit payments` : '';
          const desc = `${this.escapeHtml(d.date)}: ${d.deposits_count} deposits (${this.formatAmount(d.deposits_amount)}), ${d.payouts_count} payouts (${this.formatAmount(d.payouts_amount)})${ddp}.`;
          html += `<div class="mt-2 border-t border-border pt-2 text-xs text-muted">${desc}</div>`;
        }
      } else {
        const titleLines = tooltip.title ?? [];
        if (titleLines.length) {
          html += `<div class="mb-1.5 font-bold text-brand">${this.escapeHtml(titleLines.join(' '))}</div>`;
        }
        html += '<div class="flex flex-col gap-1">';
        tooltip.body?.forEach((body) => {
          body.lines.forEach((line) => {
            html += `<div class="m-0 flex items-center gap-1.5 text-fg-secondary"><span class="size-1.5 shrink-0 rounded-full bg-info"></span>${this.escapeHtml(line)}</div>`;
          });
        });
        html += '</div>';
        const idx = tooltip.dataPoints[0]?.dataIndex;
        if (idx !== undefined && this.polarDisplayTypes[idx]) {
          const row = this.polarDisplayTypes[idx];
          const total = this.polarDisplayTypes.reduce((a, r) => a + (r.value ?? 0), 0);
          const pct = total > 0 ? (((row.value ?? 0) / total) * 100).toFixed(1) : '0';
          const desc = `Share of all transactions in this period: ${pct}% (${row.value ?? 0} of ${total} total). Polar segments show relative volume by type.`;
          html += `<div class="mt-2 border-t border-border pt-2 text-xs text-muted">${this.escapeHtml(desc)}</div>`;
        }
      }

      el.innerHTML = html;
      el.style.opacity = '1';

      const canvas = chart.canvas;
      const rect = canvas.getBoundingClientRect();
      const left = rect.left + tooltip.caretX;
      const top = rect.top + tooltip.caretY;
      el.style.left = `${Math.min(left, window.innerWidth - 340)}px`;
      el.style.top = `${Math.min(top, window.innerHeight - 120)}px`;
    };
  }

  private escapeHtml(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private buildCharts(): void {
    if (!this.barCanvas?.nativeElement) {
      return;
    }

    const labels = this.daily.map((d) => {
      const parts = d.label.trim().split(/\s+/);
      return parts.length > 1 ? parts[1] ?? d.label : d.label;
    });

    const shouldShowDirectDebitSeries =
      this.showDirectDebit && this.daily.some((item) => (item.direct_debit_payments_count ?? 0) > 0);
    const cPrimary = cssVar('--c-primary');
    const cInfo = cssVar('--c-info');
    const cWarning = cssVar('--c-warning');
    const cGrid = cssVar('--c-border');
    const cTick = cssVar('--c-muted');
    const cLegend = cssVar('--c-fg-secondary');
    const barDatasets = [
      {
        label: 'Deposits',
        data: this.daily.map((d) => d.deposits_count),
        backgroundColor: withAlpha(cPrimary, 0.22),
        borderColor: cPrimary,
        borderWidth: 2,
        borderRadius: 5,
        borderSkipped: false,
      },
      {
        label: 'Payouts',
        data: this.daily.map((d) => d.payouts_count),
        backgroundColor: withAlpha(cInfo, 0.24),
        borderColor: cInfo,
        borderWidth: 2,
        borderRadius: 5,
        borderSkipped: false,
      },
    ];
    if (shouldShowDirectDebitSeries) {
      barDatasets.push({
        label: 'Direct Debit Payments',
        data: this.daily.map((d) => d.direct_debit_payments_count ?? 0),
        backgroundColor: withAlpha(cWarning, 0.24),
        borderColor: cWarning,
        borderWidth: 2,
        borderRadius: 5,
        borderSkipped: false,
      });
    }

    this.barChart = new Chart(this.barCanvas.nativeElement, {
      type: 'bar',
      data: {
        labels,
        datasets: barDatasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 }, usePointStyle: true, color: cLegend } },
          tooltip: {
            enabled: false,
            external: this.buildExternalTooltip('bar'),
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { maxRotation: 45, minRotation: 0, font: { size: 10 }, color: cTick },
          },
          y: {
            beginAtZero: true,
            ticks: { precision: 0, font: { size: 10 }, color: cTick },
            grid: { color: cGrid },
          },
        },
      },
    });

    const polarCanvas = this.polarCanvas?.nativeElement;
    if (!polarCanvas) {
      return;
    }

    const types = this.transactionTypes.filter((t) => (t.value ?? 0) > 0);
    this.polarDisplayTypes = types;
    if (types.length === 0) {
      return;
    }

    const polarLabels = types.map((t) => t.name);
    const polarData = types.map((t) => t.value ?? 0);
    const polarColors = types.map((_, i) => cssVar(TYPE_POLAR_TOKENS[i % TYPE_POLAR_TOKENS.length]));

    this.polarChart = new Chart(polarCanvas, {
      type: 'polarArea',
      data: {
        labels: polarLabels,
        datasets: [
          {
            data: polarData,
            backgroundColor: polarColors.map((c) => withAlpha(c, 0.6)),
            borderColor: polarColors,
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 10 }, usePointStyle: true, color: cLegend } },
          tooltip: {
            enabled: false,
            external: this.buildExternalTooltip('polar'),
          },
        },
        scales: {
          r: {
            beginAtZero: true,
            ticks: { display: true, font: { size: 9 }, color: cTick, backdropColor: 'transparent' },
            grid: { color: cGrid },
            angleLines: { color: cGrid },
          },
        },
      },
    });
  }
}
